import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import client from '../../lib/feathers';

const wineColors = {
  /* Whites (light → dark) */
  'almost clear': '#D6D9AC',
  'pale straw': '#E0DDA0',
  'green yellow': '#C7CA83',
  'light green-gold': '#C9C67A',
  'platinum yellow': '#D7CD86',
  'pale yellow': '#E2D890',
  'pale gold': '#E6B14C',
  'golden': '#D4A017',
  'deep gold': '#E8A016',
  /* Rosé & Amber */
  'pale salmon': '#E9A372',
  'deep pink': '#CD2D50',
  'deep salmon': '#D3302B',
  'tawny': '#892507',
  /* Reds (light → dark) */
  'light ruby': '#B82040',
  'ruby': '#9B1B30',
  'garnet': '#7B1A2C',
  'pale ruby': '#8E0811',
  'deep ruby': '#6B0A1A',
  'deep violet': '#3C030A',
  'dark purple': '#2A0410',
  'deep purple': '#1C0508',
};

const colorOptions = Object.keys(wineColors);

export default function NewVarietalForm(props) {
  const { onCancelClick, onNewVarietal, onUpdateVarietal, varietal } = props;
  const [name, setName] = useState('');
  const [color, setColor] = useState('');

  const isEditing = !!varietal;

  useEffect(() => {
    if (varietal) {
      setName(varietal.name || '');
      setColor(varietal.color || '');
    }
  }, [varietal]);

  const onFormSubmit = async (event) => {
    event.preventDefault();
    const service = client.service('admin/varietal');

    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!color) {
      toast.error('Please select a color');
      return;
    }

    const data = { name: name.trim(), color };

    try {
      if (isEditing) {
        const result = await service.patch(varietal.id, data);
        onUpdateVarietal(result);
        toast.success(`Updated ${name}`);
      } else {
        const result = await service.create(data);
        onNewVarietal(result);
        toast.success(`Added ${name}`);
      }
    } catch (e) {
      console.error('Error saving varietal:', e);
      toast.error(isEditing ? 'Failed to update varietal' : 'Failed to create varietal');
    }
  };

  return (
    <>
      <div className="mt-3 text-center sm:mt-5">
        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
          {isEditing ? 'Edit Varietal' : 'Create New Varietal'}
        </h3>
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            {isEditing ? 'Update the varietal details.' : 'Enter the details for this varietal.'}
          </p>
          <form onSubmit={onFormSubmit} className="mt-4">
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 text-left">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                name="name"
                id="name"
                autoComplete="off"
                placeholder="e.g., Cabernet Sauvignon"
                className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="color" className="block text-sm font-medium text-gray-700 text-left">
                Color
              </label>
              <div className="mt-1 flex items-center">
                {color && (
                  <span
                    className="inline-block w-6 h-6 rounded-full border border-gray-300 mr-2 flex-shrink-0"
                    style={{ backgroundColor: wineColors[color] || '#ccc' }}
                  />
                )}
                <select
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  required
                  name="color"
                  id="color"
                  className="focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                >
                  <option value="">Select a color</option>
                  {colorOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:col-start-2 sm:text-sm"
              >
                {isEditing ? 'Save' : 'Create'}
              </button>
              <button
                type="button"
                onClick={onCancelClick}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
